import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

interface AppVersionControl {
  min_required_version: string;
  update_message: string | null;
  store_url: string | null;
  is_critical: boolean;
}

interface ForceUpdateState {
  required: boolean;
  loading: boolean;
  message: string | null;
  storeUrl: string | null;
}

function compareVersions(current: string, minimum: string): number {
  const a = current.split('.').map(Number);
  const b = minimum.split('.').map(Number);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const diff = (a[i] || 0) - (b[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

// TODO: Quitar antes de produccion
const TEST_MODE = false;

export function useForceUpdate(): ForceUpdateState {
  const [state, setState] = useState<ForceUpdateState>({
    required: false,
    loading: true,
    message: null,
    storeUrl: null,
  });

  useEffect(() => {
    const platform = Capacitor.getPlatform();

    // Only check on native platforms
    if (platform !== 'ios' && platform !== 'android') {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    async function checkVersion() {
      let appVersion: string;
      try {
        const info = await App.getInfo();
        appVersion = info.version;
      } catch {
        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      // Modo prueba: simula actualizacion critica en cualquier plataforma
      if (TEST_MODE) {
        const needsUpdate = compareVersions(appVersion, '2.0.0') < 0;
        setState({
          required: needsUpdate,
          loading: false,
          message: 'Hay una nueva version con mejoras importantes. Actualiza para continuar.',
          storeUrl: 'https://apps.apple.com/app/tu-app-id',
        });
        return;
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as unknown as { from: (table: string) => any })
          .from('app_version_control')
          .select('min_required_version, update_message, store_url, is_critical')
          .eq('platform', platform)
          .eq('is_critical', true)
          .single() as { data: AppVersionControl | null; error: unknown };

        if (error || !data) {
          setState(prev => ({ ...prev, loading: false }));
          return;
        }

        const needsUpdate = compareVersions(appVersion, data.min_required_version) < 0;

        setState({
          required: needsUpdate,
          loading: false,
          message: data.update_message,
          storeUrl: data.store_url,
        });
      } catch {
        setState(prev => ({ ...prev, loading: false }));
      }
    }

    void checkVersion();
  }, []);

  return state;
}